import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridRowId, type GridRenderCellParams, GridInputRowSelectionModel,GridRowSelectionModel  } from "@mui/x-data-grid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { LularDayPicker } from "./LularDatePicker";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import * as XLSX from 'xlsx'
import { FileUploader } from "./FileUploader";
import { BtcTvRecord, getBtcTv } from "../../services/btc/btcApi";
import { addOrUpdateBtcDk, addOrUpdateBtcTv } from "../../features/btc/btcSlice";
import { useAppDispatch } from "../../app/hooks";
import { useIsMobile } from "../hooks/useIsMobile";
import { selectGrp } from "../../features/auth/authSlice";

/* ======================
   Types
====================== */
type ImportTvStepProps = {
  onImport: (records: BtcTvRecord[]) => void;
};

type CheckState = "new" | "conflict" | "error";

type CheckResult = {
  id: string;
  state: CheckState;
  candidateIdx: number;
  candidates: BtcTvRecord[];
};
type CommentAuthor = {
  name: string,
};
type CommentNode = {
  id: string,
  reactionsCount: number,
  subCommentsCount: number,
  content: string;
  url:string,
  children: CommentNode[];
  author: CommentAuthor
};

type PostRecord = {
  post_id: string,
  content: string,
  commentCount: number,
  comments: CommentNode[],
  url:string,
  [key: string]: unknown
};

type CommentRow = {
  id: string;
  title: string;
  question: string;
  answer: string;
  url: string;
  reactionsCount: number;
  subCommentsCount: number;
  paragraphs: string[];
  [key: string]: unknown;
};

type SumRecord = {
  traloi: number,
  like: number,
  chua: number
};

/* ======================
   Component
====================== */
const steps = ["Upload", "Chọn", "Kiểm tra", "Hoàn thành"];
const FIELDS = [
  { key: "email", alias: "Tài khoản" },
  { key: "tp", alias: "Thành phần" },
  { key: "ms", alias: "Mã số" },
  { key: "role", alias: "Role" },
  { key: "note", alias: "Ghi chú" },
]
export function ImportTvStep({ onImport }: ImportTvStepProps) {
  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [previewData, setPreviewData] = useState<CommentRow[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [excelColumns, setExcelColumns] = useState<any[]>([]);
  const [success, setSuccess] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [columnMap, setColumnMap] = useState<any>({});
  const [selectRows, setSelectRows] = useState<any[]>([]);
  const [selectColumns, setSelectColumns] = useState<any[]>([]);
  const [checkResults, setCheckResults] = useState<CheckResult[]>([]);
  const grp = useSelector(selectGrp);
  /* ======================
     STEP 1: Upload JSON
  ====================== */
  const handleUpload = (file: File|null) => {
    // const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt: ProgressEvent<FileReader>) => {
      const result = evt.target?.result;

      // console.log("file content", result);
      const arr = String(result).split(/\r?\n/);
      // remove empty row, empty column
      const rawData = clean2DArray(arr.map(row=>row.split("\t")));
      
      const maxColumn = Math.max(...rawData.map((row) => row.length), 0);

      const excelColumns = Array.from(
        { length: maxColumn },
        (_, i) => `col_${i}`,
      );

      const previewData = rawData.slice(0, 10).map((row, rowIndex) => {
        const obj = { id: rowIndex } as any;

        excelColumns.forEach((col, colIndex) => {
          obj[col] = row[colIndex] ?? "";
        });

        return obj;
      });

      setExcelColumns(excelColumns);
      setPreviewData(previewData);
      setRawData(rawData);
      nextStep();
    };

    reader.readAsText(file);
  };

  // STEP 2: chọn records
  const selectDks = () => {
    const mappedColumns = Object.entries(columnMap)
      .filter(([_, field]) => field) // chỉ giữ cột có field
      .map(([excelCol, field]) => ({
        excelCol,
        field,
      }));

    const selectRows = rawData.map((row, rowIndex) => {
      const obj = { id: rowIndex } as any;

      mappedColumns.forEach(({ excelCol, field }) => {
        const colIndex = Number(excelCol.replace("col_", ""));
        obj[String(field)] = row[colIndex] ?? "";
      });

      return obj;
    });

    const selectColumns = mappedColumns.map(({ field }) => ({
      field,
      headerName: String(field).toUpperCase(),
      minWidth: 160,
      flex: 1,
    }));
    setSelectRows(selectRows);
    setSelectColumns(selectColumns);
    nextStep();
  };

  /* ======================
     STEP 3: Check existence
  ====================== */
  const handleCheck = async () => {
    console.log("handleCheck");

    const results = await Promise.all(
      selectedIds.map(async (i) => {
        const row = selectRows[Number(i)];
        const r = checkResults.find((r) => r.id === row.id);
        if (r) {
          return r;
        }

        // if is checked skip
        const existed = checkResults.find((r) => r.id === row.id);
        if (existed) return existed;

        const { result, error } = await getBtcTv(row.email, grp);
        console.log("findRecord", row.email, result);
        const records = result ? [result] : [];

        const newResult = {
          id: row.id,
          candidateIdx: 0,
          state: "error",
          candidates: [],
        } as CheckResult;

        // transform to BtcTvRecord
        const newRecord = {
          email: row.email,
          tp: row.tp,
          role: row.role,
          note: row.note || "",
          id: `tmp_${crypto.randomUUID()}`,
        } as BtcTvRecord;

        if (records.length > 0) {
          newResult.state = "conflict";
          newResult.candidates = [newRecord, ...records];

          // update store
          dispatch(addOrUpdateBtcTv(records));
        } else {
          newResult.state = "new";
          newResult.candidates = [newRecord];
        }
        
        return newResult;
      }),
    );

    setCheckResults(results);
    nextStep();
  };

  const nextStep = () => setActiveStep(activeStep + 1);
  const prevStep = () => setActiveStep(activeStep - 1);

  const columns = excelColumns.map((col) => ({
    field: col,
    minWidth: 160,
    flex: 1,
    sortable: false,

    renderHeader: () => (
      <Select
        size="small"
        value={String(columnMap[String(col)]) || ""}
        onChange={(e) =>
          setColumnMap((prev : any) => ({
            ...prev,
            [col]: e.target.value,
          }))
        }
      >
        <MenuItem value="">-- Không import --</MenuItem>
        {FIELDS.map((f) => (
          <MenuItem key={f.key} value={f.key}>
            {f.alias}
          </MenuItem>
        ))}
      </Select>
    ),
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexGrow: 10,
          }}
        >
          {/* preview */}
          <Box sx={{ flexGrow: 1 }}>
            {/* drag/drop */}
            <FileUploader
              accept={".json"}
              onChange={handleUpload}
            ></FileUploader>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {rawData ? (
              <Button variant="outlined" onClick={() => setRawData([])}>
                Clear
              </Button>
            ) : (
              <Box></Box>
            )}

            <Stack direction={"row"} spacing={1}>
              <Button
                disabled={!rawData}
                variant="contained"
                onClick={nextStep}
              >
                Tiếp tục
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Gán tên cột
          </Typography>

          <DataGrid
            rows={previewData}
            columns={columns}
            disableRowSelectionOnClick
            autoHeight // ✅ grid tự co chiều cao
            hideFooter // ✅ Ẩn toàn bộ footer (pagination)
            disableColumnMenu // ✅ Không cần menu
            density="compact"
            columnHeaderHeight={88}
          />
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={prevStep}>
              ◀ Back (Upload)
            </Button>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => selectDks()}
            >
              Tiếp tục
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box>
          <Typography>Chọn DK muốn import</Typography>

          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={selectRows}
              columns={selectColumns}
              checkboxSelection
              disableRowSelectionOnClick
              density="compact"
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={(ids) => {
                setSelectedIds(ids);
              }}
              autoPageSize
            />
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={prevStep}>
              ◀ Back (Chọn column)
            </Button>

            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={handleCheck}
            >
              Check ({selectedIds.length})
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 3 && (
        <>
          <CheckTable
            checkResults={checkResults}
            setCheckResults={setCheckResults}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
            <Button onClick={prevStep}>BACK</Button>
            <Button
              variant="contained"
              onClick={() => {
                const records = selectedIds.map((id) => {
                  const r = checkResults.find((x) => x.id === id)!;
                  return r.candidates[r.candidateIdx];
                });

                onImport(records);
              }}
            >
              Import ({selectedIds.length})
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={success}
        autoHideDuration={3000}
        message="Import TV thành công"
        onClose={() => setSuccess(false)}
      />
    </Box>
  );
}

/* ======================
   CheckTable
====================== */
type CheckTableProps = {
  checkResults: CheckResult[];
  setCheckResults: React.Dispatch<React.SetStateAction<CheckResult[]>>;
};

function CheckTable({ checkResults, setCheckResults }: CheckTableProps) {
  console.log("CheckTable");

  const handleCandidateChange = (id: string, candidateIdx: number) => {
    setCheckResults((prev) =>
      prev.map((r) => (r.id === id ? { ...r, candidateIdx } : r)),
    );
  };

  const rows = checkResults.map((r) => {
    const record = r.candidates[r.candidateIdx];
    return {
      ...record,
      state: r.state,
      candidateIdx: r.candidateIdx,
      options: r.candidates.map((_, idx: number) => ({
        value: idx,
        label: `[${idx}] ${idx===0?"Bản mới": "Bản cũ"}`,
      })),
      resultId: r.id,
    };
  });

  const columns = [
    {
      field: "state",
      headerName: "Trạng thái",
      width: 40,
      renderCell: (params: GridRenderCellParams) =>
        params.value === "new" ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : params.value === "conflict" ? (
          <WarningAmberIcon color="warning" fontSize="small" />
        ) : null,
    },
    {
      field: "email",
      headerName: "Tài khoản",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.email;
      },
    },
    {
      field: "tp",
      headerName: "Thành phần",
      flex: 1,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
    },
    {
      field: "note",
      headerName: "Ghi chú",
      flex: 1,
    },
    {
      field: "Phiên bản",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Select
          size="small"
          fullWidth
          value={params.row.candidateIdx}
          onChange={(e) =>
            handleCandidateChange(params.row.resultId, Number(e.target.value))
          }
        >
          {params.row.options.map((opt: any) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
  ];

  return <DataGrid rows={rows} columns={columns} autoHeight disableRowSelectionOnClick />;
}


function parseCSV(csv: string): string[][] {
  return csv
    .trim()
    .split(/\r?\n/)
    .map(line =>
      line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)?.map(s =>
        s.replace(/^"(.*)"$/, "$1").replace(/""/g, '"')
      ) || []
    );
}


function clean2DArray(data: string[][]): string[][] {
  if (!data || data.length === 0) return [];

  // 1. Remove empty rows
  const nonEmptyRows = data.filter(row =>
    row.some(cell => cell != null && cell.trim() !== "")
  );

  if (nonEmptyRows.length === 0) return [];

  // 2. Find columns to keep
  const colCount = Math.max(...nonEmptyRows.map(r => r.length));

  const colsToKeep: number[] = [];

  for (let col = 0; col < colCount; col++) {
    const hasValue = nonEmptyRows.some(row => {
      const cell = row[col];
      return cell != null && cell.trim() !== "";
    });

    if (hasValue) colsToKeep.push(col);
  }

  // 3. Remove empty columns
  return nonEmptyRows.map(row =>
    colsToKeep.map(col => row[col] ?? "")
  );
}